/**
 * WebUSB Print Service - Direct thermal printer communication
 * No QZ Tray needed, no popups, works in Chrome/Edge
 */

let usbDevice = null;
let usbInterface = null;
let usbEndpoint = null;

// Known thermal printer vendor IDs
const THERMAL_PRINTER_FILTERS = [
  { vendorId: 0x04B8 }, // Epson
  { vendorId: 0x0519 }, // Star Micronics
  { vendorId: 0x0DD4 }, // Bixolon
  { vendorId: 0x0FE6 }, // Rongta / ICS
  { vendorId: 0x0483 }, // STMicroelectronics (generic thermal)
  { vendorId: 0x1FC9 }, // NXP (some printers)
  { vendorId: 0x0416 }, // Winbond (some printers)
  { vendorId: 0x20D1 }, // Custom thermal
  { vendorId: 0x154F }, // SNBC
  { vendorId: 0x1A86 }, // CH340/CH341 (very common generic Chinese printers)
  { vendorId: 0x067B }, // Prolific (USB-Serial adapters used in printers)
  { vendorId: 0x10C4 }, // Silicon Labs CP210x (USB-Serial)
  { vendorId: 0x0403 }, // FTDI (USB-Serial)
  { vendorId: 0x1CBE }, // Generic thermal
  { vendorId: 0x0456 }, // Analog Devices
  { vendorId: 0x4348 }, // WinChipHead
  { vendorId: 0x28E9 }, // GD32 (generic Chinese)
  { vendorId: 0x0525 }, // PLX Technology
  { vendorId: 0x2207 }, // Rockchip
  { vendorId: 0x1D90 }, // Citizen
  { vendorId: 0x0DD4 }, // Custom
];

/**
 * Check if WebUSB is available in this browser
 */
export function isWebUSBSupported() {
  return !!navigator.usb;
}

/**
 * Request user to select a USB printer (shows browser's native device picker)
 * This MUST be called from a user gesture (click handler)
 */
export async function requestUSBPrinter() {
  if (!navigator.usb) {
    throw new Error("WebUSB nao suportado. Use Chrome ou Edge.");
  }

  try {
    let device;
    try {
      // Try showing ALL USB devices first (Chrome 120+)
      device = await navigator.usb.requestDevice({
        filters: []
      });
    } catch (emptyFilterErr) {
      // Fallback: try with known printer vendors + common generic IDs
      console.log("[USB] Empty filter failed, trying known vendors...");
      try {
        device = await navigator.usb.requestDevice({
          filters: THERMAL_PRINTER_FILTERS
        });
      } catch (vendorErr) {
        // Last resort: try with USB printer class code
        device = await navigator.usb.requestDevice({
          filters: [{ classCode: 7 }]
        });
      }
    }
    
    await openUSBDevice(device);
    
    // Save device info for reconnection
    localStorage.setItem("usb_printer_name", device.productName || "Impressora USB");
    localStorage.setItem("usb_printer_vendor", device.vendorId?.toString());
    localStorage.setItem("usb_printer_product", device.productId?.toString());
    
    return {
      name: device.productName || "Impressora Termica USB",
      vendorId: device.vendorId,
      productId: device.productId,
      connected: true
    };
  } catch (err) {
    if (err.name === "NotFoundError") {
      throw new Error("Nenhuma impressora selecionada. Tente novamente.");
    }
    throw err;
  }
}

/**
 * Open and configure USB device for printing
 */
async function openUSBDevice(device) {
  await device.open();
  
  // Select first configuration
  if (device.configuration === null) {
    await device.selectConfiguration(1);
  }
  
  // Find the right interface (usually interface 0)
  let iface = null;
  let endpoint = null;
  
  for (const intf of device.configuration.interfaces) {
    for (const alt of intf.alternates) {
      // Look for a bulk OUT endpoint (used for printing)
      const outEp = alt.endpoints.find(ep => ep.direction === "out" && ep.type === "bulk");
      if (outEp) {
        iface = intf;
        endpoint = outEp;
        break;
      }
    }
    if (endpoint) break;
  }
  
  if (!iface || !endpoint) {
    throw new Error("Nao encontrou endpoint de impressao no dispositivo USB");
  }
  
  await device.claimInterface(iface.interfaceNumber);
  
  usbDevice = device;
  usbInterface = iface;
  usbEndpoint = endpoint;
}

/**
 * Try to reconnect to previously paired printer
 */
export async function reconnectUSBPrinter() {
  if (!navigator.usb) return null;
  
  try {
    const devices = await navigator.usb.getDevices();
    if (devices.length > 0) {
      // Try to open the first available device
      for (const device of devices) {
        try {
          await openUSBDevice(device);
          return {
            name: device.productName || "Impressora USB",
            vendorId: device.vendorId,
            productId: device.productId,
            connected: true
          };
        } catch {
          // Try next device
          continue;
        }
      }
    }
  } catch {
    // Silently fail
  }
  return null;
}

/**
 * Check if USB printer is connected and ready
 */
export function isUSBPrinterConnected() {
  return usbDevice !== null && usbDevice.opened;
}

/**
 * Get connected printer info
 */
export function getUSBPrinterInfo() {
  if (!usbDevice) return null;
  return {
    name: usbDevice.productName || localStorage.getItem("usb_printer_name") || "Impressora USB",
    vendorId: usbDevice.vendorId,
    productId: usbDevice.productId,
    connected: usbDevice.opened
  };
}

/**
 * Disconnect USB printer
 */
export async function disconnectUSBPrinter() {
  if (usbDevice && usbDevice.opened) {
    try {
      await usbDevice.close();
    } catch {}
  }
  usbDevice = null;
  usbInterface = null;
  usbEndpoint = null;
  localStorage.removeItem("usb_printer_name");
}

// --- ESC/POS Command Helpers ---
const ESC = 0x1B;
const GS = 0x1D;
const LF = 0x0A;

function textToBytes(text) {
  const encoder = new TextEncoder();
  return encoder.encode(text);
}

function escposInit() {
  return new Uint8Array([ESC, 0x40]); // Initialize printer
}

function escposCenter() {
  return new Uint8Array([ESC, 0x61, 0x01]); // Center align
}

function escposLeft() {
  return new Uint8Array([ESC, 0x61, 0x00]); // Left align
}

function escposBoldOn() {
  return new Uint8Array([ESC, 0x45, 0x01]);
}

function escposBoldOff() {
  return new Uint8Array([ESC, 0x45, 0x00]);
}

function escposCut() {
  return new Uint8Array([GS, 0x56, 0x41, 0x03]); // Partial cut with feed
}

function escposNewLine() {
  return new Uint8Array([LF]);
}

function escposFeedLines(n) {
  return new Uint8Array([ESC, 0x64, n]); // Feed n lines
}

/**
 * Build ESC/POS receipt from order data
 */
function buildReceiptData(order, options = {}) {
  const chunks = [];
  const cols = options.columns || 32;
  
  const addBytes = (bytes) => chunks.push(bytes);
  const addText = (text) => chunks.push(textToBytes(text));
  const addLine = () => addBytes(escposNewLine());
  
  function padRight(text, width) {
    return text.substring(0, width).padEnd(width);
  }
  
  function leftRight(left, right) {
    const space = Math.max(1, cols - left.length - right.length);
    return left + " ".repeat(space) + right;
  }
  
  // Initialize
  addBytes(escposInit());
  
  // Store name (centered, bold)
  if (options.storeName) {
    addBytes(escposCenter());
    addBytes(escposBoldOn());
    addText(options.storeName.toUpperCase());
    addLine();
    addBytes(escposBoldOff());
    if (options.cif) {
      addText("CIF: " + options.cif);
      addLine();
    }
    addBytes(escposLeft());
  }
  
  // Separator
  addText("=".repeat(cols));
  addLine();
  
  // Order info
  addBytes(escposBoldOn());
  addText("Pedido: #" + (order.id || "").slice(-6).toUpperCase());
  addLine();
  addBytes(escposBoldOff());
  
  if (order.customer_name) {
    addText("Cliente: " + order.customer_name);
    addLine();
  }
  if (order.table_number) {
    addText("Mesa: " + order.table_number);
    addLine();
  }
  if (order.notes) {
    addText("Nota: " + order.notes.substring(0, cols - 6));
    addLine();
  }
  
  const date = order.created_at 
    ? new Date(order.created_at).toLocaleString("pt-BR") 
    : new Date().toLocaleString("pt-BR");
  addText("Data: " + date);
  addLine();
  
  // Separator
  addText("=".repeat(cols));
  addLine();
  
  // Items header
  addBytes(escposCenter());
  addBytes(escposBoldOn());
  addText("ITENS");
  addLine();
  addBytes(escposBoldOff());
  addBytes(escposLeft());
  addText("-".repeat(cols));
  addLine();
  
  // Items
  const items = order.items || [];
  for (const item of items) {
    const qty = item.quantity || 1;
    const price = item.unit_price || 0;
    const total = (qty * price).toFixed(2);
    const name = (item.product_name || "Item").substring(0, cols - 12);
    addText(qty + "x " + name);
    addLine();
    addText(leftRight("", total + " EUR"));
    addLine();
    if (item.notes || item.observations) {
      addText("  > " + (item.notes || item.observations).substring(0, cols - 4));
      addLine();
    }
  }
  
  addText("-".repeat(cols));
  addLine();
  
  // Totals
  if (order.delivery_fee && order.delivery_fee > 0) {
    addText(leftRight("Subtotal:", (order.subtotal || order.total || 0).toFixed(2) + " EUR"));
    addLine();
    addText(leftRight("Entrega:", order.delivery_fee.toFixed(2) + " EUR"));
    addLine();
  }
  
  addBytes(escposBoldOn());
  addText(leftRight("TOTAL:", (order.total || 0).toFixed(2) + " EUR"));
  addLine();
  addBytes(escposBoldOff());
  
  if (order.payment_method) {
    addText("Pagamento: " + order.payment_method.toUpperCase());
    addLine();
  }
  
  addText("=".repeat(cols));
  addLine();
  
  // Footer
  addBytes(escposCenter());
  addText(options.receiptFooter || "Obrigado pela preferencia!");
  addLine();
  
  // Feed and cut
  addBytes(escposFeedLines(4));
  addBytes(escposCut());
  
  // Combine all chunks into single Uint8Array
  const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  
  return result;
}

/**
 * Print order receipt via WebUSB
 */
export async function printViaUSB(order, options = {}) {
  if (!usbDevice || !usbDevice.opened || !usbEndpoint) {
    // Try to reconnect
    const reconnected = await reconnectUSBPrinter();
    if (!reconnected) {
      throw new Error("Impressora USB nao conectada. Conecte primeiro.");
    }
  }
  
  const receiptData = buildReceiptData(order, options);
  const copies = options.copies || 1;
  
  for (let i = 0; i < copies; i++) {
    await usbDevice.transferOut(usbEndpoint.endpointNumber, receiptData);
    if (copies > 1 && i < copies - 1) {
      // Small delay between copies
      await new Promise(r => setTimeout(r, 500));
    }
  }
  
  return { success: true, method: "webusb", printer: usbDevice.productName };
}

/**
 * Print test page via WebUSB
 */
export async function printTestPageUSB() {
  if (!usbDevice || !usbDevice.opened) {
    throw new Error("Impressora USB nao conectada");
  }
  
  const testOrder = {
    id: "TEST-KAISO-123456",
    customer_name: "Teste Impressao",
    items: [
      { product_name: "Item Teste 1", quantity: 2, unit_price: 10.50 },
      { product_name: "Item Teste 2", quantity: 1, unit_price: 15.00, notes: "Sem cebola" },
    ],
    total: 36.00,
    payment_method: "cartao",
    created_at: new Date().toISOString(),
  };
  
  return await printViaUSB(testOrder, {
    storeName: "KAISO SYSTEM",
    receiptFooter: "Teste de impressao OK!",
  });
}
